import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminPartnersService } from "@/services/admin-partners-service";
import type {
  AdminHotelPartnerSyncStatus,
  UpdateAdminHotelAutoSyncRequest,
} from "@/types/partner-settings";
import { isTerminalPartnerSyncState } from "@/types/partner-settings";
import type { PartnerProviderLogsFilters } from "@/types/partner-provider-log";
import { describeError } from "../utils/describeError";

// Task 0036 — Guesteria-admin-only hotel sync management. Contract
// 2026-09-29. Same self-polling shape as `useAiStatus`/`useDestinationAttractions`:
// the status response's own `state` drives `refetchInterval`.
const HOTEL_SYNC_POLL_INTERVAL_MS = 5000;

export const adminHotelSyncKeys = {
  all: ["adminHotelPartnerSync"] as const,
  status: (hotelId: string, tenantId: string) =>
    [...adminHotelSyncKeys.all, "status", hotelId, tenantId] as const,
  logs: (tenantId: string, filters: PartnerProviderLogsFilters) =>
    [...adminHotelSyncKeys.all, "logs", tenantId, filters] as const,
};

export const useAdminHotelSyncStatus = (
  hotelId: string | undefined,
  tenantId: string | undefined,
) => {
  return useQuery<AdminHotelPartnerSyncStatus>({
    queryKey: adminHotelSyncKeys.status(hotelId || "", tenantId || ""),
    enabled: !!hotelId && !!tenantId,
    queryFn: () => adminPartnersService.getHotelSyncStatus(hotelId!, tenantId!),
    staleTime: 15 * 1000,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return isTerminalPartnerSyncState(state)
        ? false
        : HOTEL_SYNC_POLL_INTERVAL_MS;
    },
  });
};

export const useTriggerAdminHotelSync = (
  hotelId: string | undefined,
  tenantId: string | undefined,
) => {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      if (!hotelId || !tenantId) {
        throw new Error("hotelId and tenantId are required");
      }
      return adminPartnersService.triggerHotelSync(hotelId, tenantId);
    },
    onSuccess: () => {
      if (!hotelId || !tenantId) return;
      qc.invalidateQueries({
        queryKey: adminHotelSyncKeys.status(hotelId, tenantId),
      });
      toast.success(t("partners.admin.hotelSync.syncStarted"));
    },
    onError: (error) => {
      toast.error(
        describeError(t("partners.admin.hotelSync.syncError"), error),
      );
    },
  });

  return {
    triggerSync: () => mutation.mutate(),
    isTriggering: mutation.isPending,
  };
};

export const useUpdateAdminHotelAutoSync = (
  hotelId: string | undefined,
  tenantId: string | undefined,
) => {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: UpdateAdminHotelAutoSyncRequest) => {
      if (!hotelId || !tenantId) {
        throw new Error("hotelId and tenantId are required");
      }
      return adminPartnersService.updateHotelAutoSync(hotelId, tenantId, data);
    },
    onSuccess: () => {
      if (!hotelId || !tenantId) return;
      qc.invalidateQueries({
        queryKey: adminHotelSyncKeys.status(hotelId, tenantId),
      });
      toast.success(t("partners.admin.hotelSync.autoSyncSaved"));
    },
    onError: (error) => {
      toast.error(
        describeError(t("partners.admin.hotelSync.autoSyncError"), error),
      );
    },
  });

  return {
    setAutoSync: (enabled: boolean) => mutation.mutate({ enabled }),
    isSavingAutoSync: mutation.isPending,
  };
};

export const useAdminPartnerLogs = (
  tenantId: string | undefined,
  filters: PartnerProviderLogsFilters,
) => {
  return useQuery({
    queryKey: adminHotelSyncKeys.logs(tenantId || "", filters),
    enabled: !!tenantId,
    queryFn: () => adminPartnersService.getPartnerLogs(tenantId!, filters),
    placeholderData: (previousData) => previousData,
  });
};

/**
 * "Sync bookings now" — synchronous (task 0033, contract 2026-09-29): the
 * response already carries the finished result (`bookings/hotels/applied/
 * failed/unrouted/errors`), nothing to poll. The caller renders `result`
 * as a summary panel rather than relying on a toast alone.
 */
export const usePollOrdersFeed = () => {
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: () => adminPartnersService.pollOrdersFeed(),
    onError: (error) => {
      toast.error(
        describeError(t("partners.admin.hotelSync.pollFeedError"), error),
      );
    },
  });

  return {
    pollFeed: () => mutation.mutate(),
    isPolling: mutation.isPending,
    result: mutation.data ?? null,
    resetResult: mutation.reset,
  };
};

// Task 0033 — booking report import, Guesteria staff only, Viator provider
// page. Contract 2026-09-29.
export const useImportOrdersReport = () => {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (file: File) => adminPartnersService.importOrdersReport(file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["adminOrdersImportHistory"] });
    },
    onError: (error) => {
      toast.error(describeError(t("partners.admin.import.error"), error));
    },
  });

  return {
    importReport: (file: File) => mutation.mutateAsync(file),
    isImporting: mutation.isPending,
    result: mutation.data ?? null,
    resetResult: mutation.reset,
  };
};

export const useOrdersImportHistory = (limit = 20) => {
  return useQuery({
    queryKey: ["adminOrdersImportHistory", limit],
    queryFn: () => adminPartnersService.getOrdersImportHistory(limit),
    staleTime: 30 * 1000,
  });
};
